/**
 * ScoreBoard — visível para o master apenas.
 *
 * Desktop: painel fixo top-right, lista participantes + totais + botão
 * [+] para abrir ScoreAdjustModal.
 * Mobile: botão compacto top-left-ish que expande em drawer.
 *
 * Evita colidir com o toggle "Assumir controle" (top-right em mobile,
 * bottom-right em desktop) ajustando posição:
 *   - Desktop: bottom-right acima do toggle (toggle é bottom-16, board é bottom-72)
 *   - Mobile: drawer centralizada ao clicar no pill.
 *
 * Player não renderiza este componente; vê só o próprio XP via XpBadge.
 */

import { useState } from 'react'

import { useIsMobile } from '../hooks/useIsMobile'
import { useScore } from '../hooks/useScore'
import { useSession } from '../session'
import { ScoreAdjustModal } from './ScoreAdjustModal'
import { ThemeToggle } from './ThemeToggle'

async function exportSessionEvents(sessionId: string): Promise<void> {
  const token = localStorage.getItem('auth_token')
  if (!token) {
    alert(
      'Você precisa estar logado como mestre para exportar (auth_token ausente).',
    )
    return
  }
  try {
    const res = await fetch(
      `/api/modules/module_lab/sessions/${encodeURIComponent(sessionId)}/events.json`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (!res.ok) {
      alert(`Falha no export: HTTP ${res.status}`)
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `module_lab-session-${sessionId.slice(0, 8)}-events.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    alert(`Erro ao exportar: ${err instanceof Error ? err.message : String(err)}`)
  }
}

export function ScoreBoard() {
  const isMobile = useIsMobile()
  const { rows } = useScore()
  const session = useSession()
  // Começa recolhido em ambos (desktop e mobile): o painel fixo cobria o
  // slide durante a apresentação. O mestre abre quando quer ver o ranking.
  const [open, setOpen] = useState(false)
  const [adjustTarget, setAdjustTarget] = useState<{
    membershipId: string
    displayName: string | null
    total: number
  } | null>(null)

  const players = rows.filter((r) => r.role === 'player')

  // Botão compacto (recolhido): mobile no topo-esquerda, desktop no canto
  // inferior-direito (acima do toggle "Assumir controle"). Mantém o slide
  // livre durante a apresentação até o mestre abrir o ranking.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Abrir ranking"
        style={{
          position: 'fixed',
          ...(isMobile ? { top: 12, left: 12 } : { bottom: 72, right: 16 }),
          zIndex: 50,
          minHeight: 36,
          padding: '6px 10px',
          background: 'var(--color-lab-bg-2, #F2F2ED)',
          border: '1px solid var(--color-lab-rule, #D8D5CB)',
          borderRadius: 'var(--radius-lab-sm, 6px)',
          fontFamily: 'var(--font-lab-mono)',
          fontSize: 'var(--text-lab-xs, 0.75rem)',
          fontWeight: 700,
          color: 'var(--color-lab-ink, #0F1115)',
          cursor: 'pointer',
          boxShadow: 'var(--lab-shadow-sm)',
        }}
      >
        ⭐ {players.length} aluno{players.length === 1 ? '' : 's'} ▾
      </button>
    )
  }

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: 60,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(92vw, 360px)',
        maxHeight: '70vh',
        overflowY: 'auto',
        zIndex: 60,
      }
    : {
        position: 'fixed',
        bottom: 72, // acima do toggle bottom-16
        right: 16,
        maxWidth: 300,
        maxHeight: '60vh',
        overflowY: 'auto',
        zIndex: 49,
      }

  return (
    <>
      {isMobile && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 55,
          }}
          aria-hidden="true"
        />
      )}
      <div
        style={{
          ...panelStyle,
          padding: 12,
          background: 'var(--color-lab-bg-2, #F2F2ED)',
          border: '1px solid var(--color-lab-rule, #D8D5CB)',
          borderRadius: 'var(--radius-lab, 12px)',
          boxShadow: 'var(--lab-shadow, 0 8px 24px rgba(15,17,21,0.08))',
          fontFamily: 'var(--font-lab-mono, monospace)',
          color: 'var(--color-lab-ink, #0F1115)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-lab-xs, 0.75rem)',
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: 'var(--color-lab-ink-3, #555B66)',
            }}
          >
            Ranking
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <ThemeToggle position="inline" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fechar ranking"
              style={{
                minWidth: 28,
                minHeight: 28,
                background: 'transparent',
                border: 'none',
                fontSize: 20,
                lineHeight: 1,
                color: 'var(--color-lab-ink-3)',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {players.length === 0 ? (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-lab-sm, 0.875rem)',
              color: 'var(--color-lab-ink-3)',
              fontFamily: 'var(--font-lab-sans)',
            }}
          >
            Nenhum aluno conectado ainda.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {players.map((r) => (
              <li
                key={r.membershipId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  background: 'var(--color-lab-bg, #FAFAF7)',
                  border: '1px solid var(--color-lab-rule, #D8D5CB)',
                  borderRadius: 'var(--radius-lab-sm, 6px)',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-lab-sm, 0.875rem)',
                    fontFamily: 'var(--font-lab-sans)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={r.displayName ?? r.membershipId}
                >
                  {r.displayName ?? r.membershipId.slice(0, 8)}
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-lab-sm, 0.875rem)',
                    fontWeight: 700,
                    color: 'var(--color-lab-accent, #5B2DB8)',
                    minWidth: 32,
                    textAlign: 'right',
                  }}
                >
                  {r.total}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setAdjustTarget({
                      membershipId: r.membershipId,
                      displayName: r.displayName,
                      total: r.total,
                    })
                  }
                  aria-label={`Ajustar XP de ${r.displayName ?? 'aluno'}`}
                  style={{
                    minWidth: 28,
                    minHeight: 28,
                    padding: 0,
                    background: 'transparent',
                    border: '1px dashed var(--color-lab-rule, #D8D5CB)',
                    borderRadius: 'var(--radius-lab-sm, 6px)',
                    color: 'var(--color-lab-ink-3, #555B66)',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  ±
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={() => exportSessionEvents(session.sessionId)}
          style={{
            marginTop: 10,
            minHeight: 36,
            padding: '6px 12px',
            background: '#FFFFFF',
            color: '#0F1115',
            border: '1px solid #D8D5CB',
            borderRadius: 'var(--radius-lab-sm, 6px)',
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-xs, 0.75rem)',
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: 0.3,
          }}
          title="Baixar todos os eventos, respostas e pontuações da sessão em JSON"
        >
          ↓ Exportar eventos (JSON)
        </button>
      </div>

      {adjustTarget && (
        <ScoreAdjustModal
          membershipId={adjustTarget.membershipId}
          displayName={adjustTarget.displayName}
          currentTotal={adjustTarget.total}
          onClose={() => setAdjustTarget(null)}
        />
      )}
    </>
  )
}
